import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Icon } from './StockControl';

const AllDifferencesModal = ({ show, onClose, allDifferencesData, onProductClick }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_90px_rgba(15,23,42,0.28)]">
        <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600">Diferencias históricas</p>
            <h3 className="mt-1 text-2xl font-bold text-slate-950">Productos con desvíos en los últimos 30 días</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-2 transition hover:bg-stone-200">
            <Icon path="M6 18L18 6M6 6l12 12" className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-grow overflow-y-auto pr-2">
          {allDifferencesData.length === 0 ? (
            <p className="py-8 text-center text-slate-500">No hay productos con diferencias registradas en este período.</p>
          ) : (
            <table className="min-w-full overflow-hidden rounded-[20px] border border-slate-200 bg-white">
              <thead className="bg-slate-950 text-[11px] uppercase tracking-[0.16em] text-cyan-200">
                <tr>
                  <th className="px-5 py-3 text-left">Producto</th>
                  <th className="px-5 py-3 text-right">Diferencia total</th>
                </tr>
              </thead>
              <tbody className="text-sm text-slate-700">
                {allDifferencesData.map((item, index) => (
                  <tr
                    key={`${item.name}-${index}`}
                    className="cursor-pointer border-b border-slate-100 transition hover:bg-slate-50"
                    onClick={() => {
                      onClose();
                      onProductClick(item.name);
                    }}
                  >
                    <td className="whitespace-nowrap px-5 py-3 font-medium">{item.name}</td>
                    <td className="px-5 py-3 text-right font-semibold text-rose-600">{item.diferencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const formatCountLabel = (value, singular, plural = singular) => `${value} ${value === 1 ? singular : plural}`;

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!amount) return '$ 0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCompactUnits = (value) => `${Number(value || 0)} u.`;

const ExecutiveCard = ({ title, value, helper, tone = 'slate', isActive = false, onClick, impact = null }) => {
  const tones = {
    slate: {
      base: 'border-slate-200 bg-white text-slate-900',
      active: 'border-slate-900 bg-slate-950 text-white shadow-[0_18px_40px_rgba(15,23,42,0.18)]',
      eyebrow: 'text-slate-500',
      eyebrowActive: 'text-slate-300',
      helper: 'text-slate-500',
      helperActive: 'text-slate-300',
    },
    cyan: {
      base: 'border-cyan-200 bg-cyan-50 text-cyan-900',
      active: 'border-cyan-600 bg-cyan-600 text-white shadow-[0_18px_40px_rgba(8,145,178,0.24)]',
      eyebrow: 'text-cyan-700',
      eyebrowActive: 'text-cyan-100',
      helper: 'text-cyan-700',
      helperActive: 'text-cyan-100',
    },
    emerald: {
      base: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      active: 'border-emerald-600 bg-emerald-600 text-white shadow-[0_18px_40px_rgba(5,150,105,0.24)]',
      eyebrow: 'text-emerald-700',
      eyebrowActive: 'text-emerald-100',
      helper: 'text-emerald-700',
      helperActive: 'text-emerald-100',
    },
    rose: {
      base: 'border-rose-200 bg-rose-50 text-rose-900',
      active: 'border-rose-600 bg-rose-600 text-white shadow-[0_18px_40px_rgba(225,29,72,0.24)]',
      eyebrow: 'text-rose-700',
      eyebrowActive: 'text-rose-100',
      helper: 'text-rose-700',
      helperActive: 'text-rose-100',
    },
    violet: {
      base: 'border-violet-200 bg-violet-50 text-violet-900',
      active: 'border-violet-600 bg-violet-600 text-white shadow-[0_18px_40px_rgba(124,58,237,0.24)]',
      eyebrow: 'text-violet-700',
      eyebrowActive: 'text-violet-100',
      helper: 'text-violet-700',
      helperActive: 'text-violet-100',
    },
  };

  const palette = tones[tone] || tones.slate;
  const impactAmount = typeof impact === 'object' ? impact?.amount : impact;
  const impactLabel = typeof impact === 'object' ? impact?.label : '';
  const impactDetail = typeof impact === 'object' ? impact?.detail : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-[24px] border px-4 py-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        isActive ? palette.active : palette.base
      }`}
    >
      <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${isActive ? palette.eyebrowActive : palette.eyebrow}`}>
        {title}
      </p>
      <p className="mt-2 text-2xl font-bold leading-none">{value}</p>
      <p className={`mt-2 text-xs ${isActive ? palette.helperActive : palette.helper}`}>{helper}</p>
      {impactAmount ? (
        <div className={`mt-3 rounded-[18px] border px-3 py-2 ${isActive ? 'border-white/20 bg-white/10' : 'border-white/80 bg-white/70'}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${isActive ? palette.eyebrowActive : palette.eyebrow}`}>
            Último impacto
          </p>
          <p className="mt-1 text-sm font-bold">{impactAmount}</p>
          {impactLabel ? <p className={`mt-1 text-xs ${isActive ? palette.helperActive : palette.helper}`}>{impactLabel}</p> : null}
          {impactDetail ? <p className={`mt-0.5 text-[11px] ${isActive ? palette.helperActive : palette.helper}`}>{impactDetail}</p> : null}
        </div>
      ) : null}
    </button>
  );
};

const formatImpactDateTime = (value) => {
  if (!value) return 'Sin fecha';
  if (typeof value?.toDate === 'function') return formatImpactDateTime(value.toDate());
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Sin fecha';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const formatImpactDayLabel = (value) =>
  new Intl.DateTimeFormat('es-AR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(value);

const groupImpactRowsByDay = (rows = []) => {
  const map = new Map();

  rows
    .filter((row) => row?.date instanceof Date && !Number.isNaN(row.date.getTime()))
    .sort((a, b) => b.date - a.date)
    .forEach((row) => {
      const key = row.date.toISOString().slice(0, 10);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: formatImpactDayLabel(row.date),
          total: 0,
          rows: [],
        });
      }
      const bucket = map.get(key);
      bucket.total += Number(row.amount || 0);
      bucket.rows.push(row);
    });

  return Array.from(map.values());
};

const StockImpactAccordion = ({ groups = [], formatAmount = formatMoney, tone = 'slate', emptyTitle, emptyDescription }) => {
  const [openDay, setOpenDay] = useState(groups[0]?.key || null);

  useEffect(() => {
    if (!groups.length) {
      setOpenDay(null);
      return;
    }
    if (!groups.some((group) => group.key === openDay)) {
      setOpenDay(groups[0].key);
    }
  }, [groups, openDay]);

  const tones = {
    emerald: {
      shell: 'border-emerald-200 bg-emerald-50/60',
      badge: 'border-emerald-200 bg-white text-emerald-700',
      amount: 'text-emerald-800',
    },
    cyan: {
      shell: 'border-cyan-200 bg-cyan-50/60',
      badge: 'border-cyan-200 bg-white text-cyan-700',
      amount: 'text-cyan-800',
    },
    violet: {
      shell: 'border-violet-200 bg-violet-50/60',
      badge: 'border-violet-200 bg-white text-violet-700',
      amount: 'text-violet-800',
    },
    rose: {
      shell: 'border-rose-200 bg-rose-50/60',
      badge: 'border-rose-200 bg-white text-rose-700',
      amount: 'text-rose-800',
    },
    slate: {
      shell: 'border-slate-200 bg-slate-50/60',
      badge: 'border-slate-200 bg-white text-slate-700',
      amount: 'text-slate-800',
    },
  };

  const palette = tones[tone] || tones.slate;

  if (!groups.length) {
    return (
      <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-5 py-6 text-center">
        <p className="text-base font-bold text-slate-900">{emptyTitle}</p>
        <p className="mt-2 text-sm text-slate-500">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const isOpen = openDay === group.key;
        return (
          <div key={group.key} className={`overflow-hidden rounded-[22px] border ${palette.shell}`}>
            <button
              type="button"
              onClick={() => setOpenDay((current) => (current === group.key ? null : group.key))}
              className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
            >
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{group.label}</p>
                <p className={`mt-1 text-lg font-black ${palette.amount}`}>{formatAmount(group.total)}</p>
                <p className="mt-1 text-xs text-slate-500">{group.rows.length} movimientos en el día</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${palette.badge}`}>
                  {isOpen ? 'Ocultar' : 'Ver detalle'}
                </span>
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                  <Icon path={isOpen ? 'M19.5 14.25l-7.5-7.5-7.5 7.5' : 'M4.5 9.75l7.5 7.5 7.5-7.5'} className="h-4 w-4" />
                </div>
              </div>
            </button>
            {isOpen ? (
              <div className="border-t border-white/70 bg-white/80 px-4 py-3">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Hora</th>
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Origen</th>
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Detalle</th>
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Referencia</th>
                        <th className="py-3 text-right text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {group.rows.map((row) => (
                        <tr key={row.id} className="align-top">
                          <td className="py-3 pr-3 text-sm font-medium text-slate-700">{formatImpactDateTime(row.date)}</td>
                          <td className="py-3 pr-3 text-sm font-semibold text-slate-900">{row.origin}</td>
                          <td className="py-3 pr-3 text-sm text-slate-600">{row.detail}</td>
                          <td className="py-3 pr-3 text-sm text-slate-500">{row.reference || 'Sin referencia'}</td>
                          <td className={`py-3 text-right text-sm font-black ${palette.amount}`}>
                            {row.sign === 'negative' ? '-' : '+'}{formatAmount(Math.abs(Number(row.amount || 0)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const RankingList = ({
  title,
  subtitle,
  rows = [],
  accent = 'text-slate-700',
  valueFormatter = (row) => row,
  secondaryFormatter = null,
  emptyText = 'Todavía no hay datos en este período.',
}) => (
  <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
    <div className="border-b border-slate-100 pb-3">
      <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${accent}`}>{title}</p>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
    </div>
    <div className="mt-3 space-y-2">
      {rows.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">{emptyText}</p>
      ) : rows.map((row, index) => (
        <div key={`${title}-${row.product || row.supplier || index}`} className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">#{index + 1}</p>
              <p className="mt-1 truncate text-sm font-semibold text-slate-900">{row.product || row.supplier || 'Sin nombre'}</p>
              {secondaryFormatter ? <p className="mt-1 text-xs text-slate-500">{secondaryFormatter(row)}</p> : null}
            </div>
            <p className={`shrink-0 text-sm font-bold ${accent}`}>{valueFormatter(row)}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SectionShell = ({ eyebrow, title, description, children, rightAction = null }) => (
  <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
    <div className="border-b border-slate-100 pb-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-600">{eyebrow}</p>
          <h4 className="mt-1 text-2xl font-bold text-slate-900">{title}</h4>
          {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
        </div>
        {rightAction}
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const StatisticsView = ({
  tasks,
  allHistory,
  onStatClick,
  onBarClick,
  weekDays,
  stockBalance,
  initialSection = 'stats',
  showSectionTabs = false,
}) => {
  const [showAllDifferencesModal, setShowAllDifferencesModal] = useState(false);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [activeStatsPanel, setActiveStatsPanel] = useState('control');
  const [activeBalancePanel, setActiveBalancePanel] = useState('commercial');

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  useEffect(() => {
    if (initialSection === 'stats') setActiveStatsPanel('control');
    if (initialSection === 'balance') setActiveBalancePanel('commercial');
  }, [initialSection]);

  const taskStatusData = useMemo(
    () => [
      { name: 'Pendientes', value: tasks.filter((task) => task.status === 'pending').length, fill: '#0f172a' },
      { name: 'Completadas', value: tasks.filter((task) => task.status === 'completed').length, fill: '#10b981' },
    ],
    [tasks]
  );

  const weeklyActivityData = useMemo(
    () =>
      weekDays.map((day) => {
        const dayString = day.toISOString().slice(0, 10);
        return {
          name: day.toLocaleDateString('es-AR', { weekday: 'short' }),
          controles: tasks.filter((task) => task.date === dayString && task.status === 'completed').length,
        };
      }),
    [tasks, weekDays]
  );

  const { allDifferencesData, topDifferencesData, totalDifferences } = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const differences = allHistory
      .filter((item) => item.endTime && item.endTime.toDate() > thirtyDaysAgo)
      .flatMap((item) => {
        try {
          const details = JSON.parse(item.countDetails);
          return details.map((detail) => ({ ...detail, productName: item.productName }));
        } catch {
          return [];
        }
      })
      .filter((item) => typeof item.difference === 'number');

    const productDifferences = differences.reduce((acc, item) => {
      acc[item.productName] = (acc[item.productName] || 0) + Math.abs(item.difference);
      return acc;
    }, {});

    const rows = Object.entries(productDifferences)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value]) => ({ name, diferencia: value }));

    return {
      allDifferencesData: rows,
      topDifferencesData: rows.slice(0, 5),
      totalDifferences: rows.reduce((acc, item) => acc + item.diferencia, 0),
    };
  }, [allHistory]);

  const overviewCards = useMemo(() => {
    const pending = taskStatusData.find((item) => item.name === 'Pendientes')?.value || 0;
    const completed = taskStatusData.find((item) => item.name === 'Completadas')?.value || 0;
    const total = pending + completed;
    const compliance = total > 0 ? Math.round((completed / total) * 100) : 100;
    const mostActiveDay = [...weeklyActivityData].sort((a, b) => b.controles - a.controles)[0];

    return [
      {
        label: 'Cumplimiento',
        value: `${compliance}%`,
        helper: `${formatCountLabel(completed, 'cierre', 'cierres')} sobre ${formatCountLabel(total, 'tarea', 'tareas')}`,
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      },
      {
        label: 'Pendientes',
        value: `${pending}`,
        helper: 'todavía esperan control en la agenda',
        tone: 'border-slate-200 bg-slate-50 text-slate-700',
      },
      {
        label: 'Diferencias',
        value: `${totalDifferences}`,
        helper: 'desvíos acumulados visibles en 30 días',
        tone: 'border-rose-200 bg-rose-50 text-rose-700',
      },
      {
        label: 'Pico semanal',
        value: `${mostActiveDay?.controles || 0}`,
        helper: mostActiveDay ? `${mostActiveDay.name} fue el día más activo` : 'sin actividad registrada',
        tone: 'border-cyan-200 bg-cyan-50 text-cyan-700',
      },
    ];
  }, [taskStatusData, totalDifferences, weeklyActivityData]);

  const stockBalanceCards = useMemo(() => ([
    {
      label: 'Costo actual del stock guardado',
      value: formatMoney(stockBalance?.currentCapitalValue || 0),
      helper: `${formatCountLabel(stockBalance?.totalStoredUnits || 0, 'unidad', 'unidades')} hoy en depósito`,
      tone: 'border-slate-200 bg-slate-50 text-slate-800',
    },
    {
      label: 'Venta estimada del stock guardado',
      value: formatMoney(stockBalance?.currentPotentialSaleValue || 0),
      helper: 'lo que podrías cobrar si hoy vendieras ese stock',
      tone: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    },
    {
      label: 'Ganancia estimada del stock guardado',
      value: formatMoney(stockBalance?.currentPotentialMargin || 0),
      helper: 'diferencia estimada entre costo y venta del stock actual',
      tone: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    },
    {
      label: `Ganancia realizada (${stockBalance?.periodLabel || 'período'})`,
      value: formatMoney(stockBalance?.realizedMargin || 0),
      helper: `${formatMoney(stockBalance?.realizedSales || 0)} vendidos sobre ${formatMoney(stockBalance?.realizedCost || 0)} de costo`,
      tone: 'border-violet-200 bg-violet-50 text-violet-800',
    },
  ]), [stockBalance]);

  const stockPeriodCards = useMemo(() => ([
    {
      label: `Gasto en compras (${stockBalance?.periodLabel || 'período'})`,
      value: `-${formatMoney(stockBalance?.purchaseSpend || 0)}`,
      helper: `${formatCountLabel(stockBalance?.purchaseUnits || 0, 'unidad', 'unidades')} ingresadas en el período`,
      tone: 'border-rose-200 bg-rose-50 text-rose-800',
    },
    {
      label: `Vendido (${stockBalance?.periodLabel || 'período'})`,
      value: formatMoney(stockBalance?.realizedSales || 0),
      helper: `${formatCountLabel(stockBalance?.realizedUnits || 0, 'unidad', 'unidades')} entregadas en el período`,
      tone: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    },
    {
      label: `Ganancia realizada (${stockBalance?.periodLabel || 'período'})`,
      value: formatMoney(stockBalance?.realizedMargin || 0),
      helper: `${formatMoney(stockBalance?.realizedSales || 0)} vendidos sobre ${formatMoney(stockBalance?.realizedCost || 0)} de costo`,
      tone: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    },
  ]), [stockBalance]);

  const stockAlertCards = useMemo(() => ([
    {
      label: 'Pendiente de ingreso',
      value: `${stockBalance?.totalPendingInboundUnits || 0}`,
      helper: 'unidades entre retiro pendiente y tránsito de compra',
      tone: 'border-amber-200 bg-amber-50 text-amber-800',
    },
    {
      label: 'Pendiente de salida',
      value: `${stockBalance?.totalPendingOutboundUnits || 0}`,
      helper: 'unidades comprometidas o en tránsito de entrega',
      tone: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800',
    },
    {
      label: 'Estados especiales',
      value: `${stockBalance?.totalSpecialUnits || 0}`,
      helper: 'garantía, devolución, reparación o rotura',
      tone: 'border-orange-200 bg-orange-50 text-orange-800',
    },
    {
      label: 'Alertas de carga',
      value: `${Number(stockBalance?.productsWithMissingCost || 0) + Number(stockBalance?.productsWithMissingSalePrice || 0)}`,
      helper: `${stockBalance?.productsWithMissingCost || 0} sin costo · ${stockBalance?.productsWithMissingSalePrice || 0} sin precio de venta`,
      tone: 'border-rose-200 bg-rose-50 text-rose-800',
    },
  ]), [stockBalance]);

  const commercialPulseCards = useMemo(() => {
    const maxBase = Math.max(
      Number(stockBalance?.purchaseSpend || 0),
      Number(stockBalance?.realizedSales || 0),
      Number(stockBalance?.realizedMargin || 0),
      1
    );
    return [
      {
        label: 'Gasto en compras',
        value: `-${formatMoney(stockBalance?.purchaseSpend || 0)}`,
        helper: `${formatCountLabel(stockBalance?.purchaseUnits || 0, 'unidad', 'unidades')} ingresadas`,
        tone: 'border-rose-200 bg-rose-50 text-rose-800',
        fill: 'from-rose-500 to-rose-300',
        width: `${Math.max(12, (Number(stockBalance?.purchaseSpend || 0) / maxBase) * 100)}%`,
      },
      {
        label: 'Vendido',
        value: formatMoney(stockBalance?.realizedSales || 0),
        helper: `${formatCountLabel(stockBalance?.realizedUnits || 0, 'unidad', 'unidades')} entregadas`,
        tone: 'border-cyan-200 bg-cyan-50 text-cyan-800',
        fill: 'from-cyan-600 to-sky-300',
        width: `${Math.max(12, (Number(stockBalance?.realizedSales || 0) / maxBase) * 100)}%`,
      },
      {
        label: 'Ganancia realizada',
        value: formatMoney(stockBalance?.realizedMargin || 0),
        helper: `${formatMoney(stockBalance?.realizedSales || 0)} vendidos sobre ${formatMoney(stockBalance?.realizedCost || 0)} de costo`,
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        fill: 'from-emerald-600 to-lime-300',
        width: `${Math.max(12, (Number(stockBalance?.realizedMargin || 0) / maxBase) * 100)}%`,
      },
    ];
  }, [stockBalance]);

  const stockBalanceTrace = useMemo(() => {
    const movements = Array.isArray(stockBalance?.periodMovements) ? stockBalance.periodMovements : [];

    const toDate = (value) => {
      if (!value) return null;
      if (typeof value?.toDate === 'function') {
        try {
          return value.toDate();
        } catch {
          return null;
        }
      }
      const parsed = value instanceof Date ? value : new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getMovementDate = (movement) =>
      toDate(movement?.appliedAt) ||
      toDate(movement?.createdAt) ||
      toDate(movement?.updatedAt) ||
      null;

    const commercialRows = [];
    const stockRows = [];
    const productRows = [];

    movements.forEach((movement, index) => {
      const date = getMovementDate(movement);
      if (!date) return;

      const movementType = String(movement?.movementType || '').trim();
      const quantity = Number(movement?.quantity || 0) || 0;
      const costTotal = Number(movement?.costTotalSnapshot || ((Number(movement?.costUnitSnapshot || movement?.unitCostSnapshot || movement?.unitCost || 0) || 0) * quantity) || 0);
      const saleTotal = Number(movement?.saleTotalSnapshot || ((Number(movement?.saleUnitSnapshot || 0) || 0) * quantity) || 0);
      const marginTotal = Number(movement?.marginSnapshot || (saleTotal - costTotal) || 0);
      const productLabel = movement?.productNameSnapshot || movement?.productName || movement?.productId || 'Producto';
      const supplierLabel = movement?.supplierNameSnapshot || movement?.supplierName || 'Sin proveedor';
      const routeLabel = movement?.routeNameSnapshot || movement?.orderId || movement?.sourceLineId || 'Sin referencia';

      if (movementType === 'purchase_received') {
        commercialRows.push({
          id: `purchase-${movement?.id || index}`,
          date,
          origin: 'Compra de stock',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: supplierLabel,
          amount: costTotal,
          sign: 'negative',
        });
        stockRows.push({
          id: `stock-in-${movement?.id || index}`,
          date,
          origin: 'Ingreso a depósito',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: supplierLabel,
          amount: quantity,
          sign: 'positive',
          amountLabel: `${quantity} u.`,
        });
        productRows.push({
          id: `product-in-${movement?.id || index}`,
          date,
          origin: 'Movimiento de producto',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: 'Ingreso recibido',
          amount: quantity,
          sign: 'positive',
          amountLabel: `${quantity} u.`,
        });
      }

      if (movementType === 'stock_outbound_delivered') {
        commercialRows.push({
          id: `sale-${movement?.id || index}`,
          date,
          origin: 'Venta de stock',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: routeLabel,
          amount: saleTotal,
          sign: 'positive',
        });
        if (marginTotal !== 0) {
          commercialRows.push({
            id: `margin-${movement?.id || index}`,
            date,
            origin: 'Margen de stock',
            detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
            reference: routeLabel,
            amount: marginTotal,
            sign: marginTotal >= 0 ? 'positive' : 'negative',
          });
        }
        stockRows.push({
          id: `stock-out-${movement?.id || index}`,
          date,
          origin: 'Salida de depósito',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: routeLabel,
          amount: quantity,
          sign: 'negative',
          amountLabel: `${quantity} u.`,
        });
        productRows.push({
          id: `product-out-${movement?.id || index}`,
          date,
          origin: 'Movimiento de producto',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: 'Salida entregada',
          amount: quantity,
          sign: 'negative',
          amountLabel: `${quantity} u.`,
        });
      }
    });

    const alertsRows = [
      Number(stockBalance?.totalPendingInboundUnits || 0) > 0
        ? {
            id: 'alert-inbound',
            amount: Number(stockBalance?.totalPendingInboundUnits || 0),
            label: `${Number(stockBalance?.totalPendingInboundUnits || 0)} pendientes`,
            detail: 'Ingresos todavía no recibidos',
          }
        : null,
      Number(stockBalance?.totalPendingOutboundUnits || 0) > 0
        ? {
            id: 'alert-outbound',
            amount: Number(stockBalance?.totalPendingOutboundUnits || 0),
            label: `${Number(stockBalance?.totalPendingOutboundUnits || 0)} pendientes`,
            detail: 'Salidas todavía no entregadas',
          }
        : null,
      Number(stockBalance?.totalSpecialUnits || 0) > 0
        ? {
            id: 'alert-special',
            amount: Number(stockBalance?.totalSpecialUnits || 0),
            label: `${Number(stockBalance?.totalSpecialUnits || 0)} unidades`,
            detail: 'Productos en reparación, devolución o dañados',
          }
        : null,
      (Number(stockBalance?.productsWithMissingCost || 0) + Number(stockBalance?.productsWithMissingSalePrice || 0)) > 0
        ? {
            id: 'alert-pricing',
            amount: Number(stockBalance?.productsWithMissingCost || 0) + Number(stockBalance?.productsWithMissingSalePrice || 0),
            label: `${Number(stockBalance?.productsWithMissingCost || 0) + Number(stockBalance?.productsWithMissingSalePrice || 0)} productos`,
            detail: `${Number(stockBalance?.productsWithMissingCost || 0)} sin costo · ${Number(stockBalance?.productsWithMissingSalePrice || 0)} sin precio`,
          }
        : null,
    ].filter(Boolean);

    const latestCommercial = [...commercialRows].sort((a, b) => b.date - a.date)[0] || null;
    const latestStock = [...stockRows].sort((a, b) => b.date - a.date)[0] || null;
    const latestProduct = [...productRows].sort((a, b) => b.date - a.date)[0] || null;
    const latestAlert = alertsRows[0] || null;

    return {
      commercialImpact: latestCommercial
        ? {
            amount: `${latestCommercial.sign === 'negative' ? '-' : '+'}${formatMoney(Math.abs(latestCommercial.amount))}`,
            label: latestCommercial.detail,
            detail: latestCommercial.reference,
          }
        : null,
      stockImpact: latestStock
        ? {
            amount: `${latestStock.sign === 'negative' ? '-' : '+'}${latestStock.amountLabel || `${Math.abs(Number(latestStock.amount || 0))} u.`}`,
            label: latestStock.detail,
            detail: latestStock.reference,
          }
        : null,
      productImpact: latestProduct
        ? {
            amount: `${latestProduct.sign === 'negative' ? '-' : '+'}${latestProduct.amountLabel || `${latestProduct.amount} u.`}`,
            label: latestProduct.detail,
            detail: latestProduct.reference,
          }
        : null,
      alertImpact: latestAlert
        ? {
            amount: latestAlert.label,
            label: latestAlert.detail,
            detail: 'Requiere revisión',
          }
        : null,
      commercialGroups: groupImpactRowsByDay(commercialRows),
      stockGroups: groupImpactRowsByDay(stockRows),
      productGroups: groupImpactRowsByDay(productRows),
      alertsRows,
    };
  }, [stockBalance]);

  const statsNavigatorCards = useMemo(() => {
    const pending = taskStatusData.find((item) => item.name === 'Pendientes')?.value || 0;
    const completed = taskStatusData.find((item) => item.name === 'Completadas')?.value || 0;
    const mostActiveDay = [...weeklyActivityData].sort((a, b) => b.controles - a.controles)[0];
    return [
      { key: 'control', title: 'Control', value: `${completed}`, helper: `${pending} pendientes · ${completed} completados`, tone: 'slate' },
      { key: 'activity', title: 'Actividad', value: `${mostActiveDay?.controles || 0}`, helper: mostActiveDay ? `${mostActiveDay.name} fue el día más cargado` : 'sin movimiento visible', tone: 'cyan' },
      { key: 'differences', title: 'Diferencias', value: `${totalDifferences}`, helper: 'desvíos acumulados en los últimos 30 días', tone: 'rose' },
      { key: 'commercial', title: 'Lectura comercial', value: `${(stockBalance?.topSellingProducts || []).length}`, helper: 'productos con ventas visibles en el período', tone: 'emerald' },
    ];
  }, [taskStatusData, weeklyActivityData, totalDifferences, stockBalance]);

  const balanceNavigatorCards = useMemo(() => ([
    { key: 'commercial', title: 'Resultado', value: formatMoney(stockBalance?.realizedMargin || 0), helper: `${formatMoney(stockBalance?.realizedSales || 0)} vendidos en ${stockBalance?.periodLabel || 'el período'}`, tone: 'emerald', impact: stockBalanceTrace.commercialImpact },
    { key: 'stock', title: 'Stock guardado', value: formatMoney(stockBalance?.currentCapitalValue || 0), helper: `${formatCountLabel(stockBalance?.totalStoredUnits || 0, 'unidad', 'unidades')} hoy en depósito`, tone: 'cyan', impact: stockBalanceTrace.stockImpact },
    { key: 'products', title: 'Productos', value: `${(stockBalance?.topSellingProducts || []).length}`, helper: 'ranking comercial del período', tone: 'violet', impact: stockBalanceTrace.productImpact },
    { key: 'alerts', title: 'Alertas', value: `${Number(stockBalance?.productsWithMissingCost || 0) + Number(stockBalance?.productsWithMissingSalePrice || 0) + Number(stockBalance?.totalPendingInboundUnits || 0) + Number(stockBalance?.totalPendingOutboundUnits || 0)}`, helper: 'pendientes y datos a revisar', tone: 'rose', impact: stockBalanceTrace.alertImpact },
  ]), [stockBalance, stockBalanceTrace]);

  return (
    <div className="w-full space-y-6 overflow-y-auto p-4 md:p-6">
      <div className="rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#172554_60%,#0f172a_100%)] px-5 py-5 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-200">Estadísticas e informes</p>
        <div className="mt-2 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-3xl font-bold">Lectura ejecutiva del stock</h3>
            <p className="mt-2 max-w-3xl text-sm text-slate-200/90">
              La idea acá es mirar el negocio sin ruido: arriba elegís el foco y abajo se abre solo ese bloque.
            </p>
          </div>
        </div>
      </div>

      {showSectionTabs ? (
        <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setActiveSection('stats')} className={activeSection === 'stats' ? 'rounded-full border border-slate-900 bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(15,23,42,0.18)]' : 'rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900'}>
              Estadísticas
            </button>
            <button type="button" onClick={() => setActiveSection('balance')} className={activeSection === 'balance' ? 'rounded-full border border-slate-900 bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(15,23,42,0.18)]' : 'rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900'}>
              Balance
            </button>
          </div>
        </div>
      ) : null}

      {activeSection === 'stats' ? (
        <>
          <SectionShell
            eyebrow="Estadísticas"
            title="Tablero de control y movimiento"
            description="Tocás una tarjeta y abajo ves solo el foco elegido: control, actividad, diferencias o lectura comercial."
          >
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {statsNavigatorCards.map((card) => (
                <ExecutiveCard
                  key={card.key}
                  title={card.title}
                  value={card.value}
                  helper={card.helper}
                  tone={card.tone}
                  isActive={activeStatsPanel === card.key}
                  onClick={() => setActiveStatsPanel(card.key)}
                />
              ))}
            </div>
          </SectionShell>

          {activeStatsPanel === 'control' ? (
            <SectionShell
              eyebrow="Control"
              title="Ritmo y cumplimiento operativo"
              description="Acá ves cómo viene el control físico: cumplimiento, pendientes y panorama general."
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {overviewCards.map((card) => (
                  <div key={card.label} className={`rounded-[22px] border px-4 py-4 shadow-sm ${card.tone}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{card.label}</p>
                    <p className="mt-2 text-2xl font-bold leading-none">{card.value}</p>
                    <p className="mt-2 text-xs opacity-80">{card.helper}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md" onClick={() => onStatClick('Resumen de Tareas', taskStatusData)}>
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-600">Estado semanal</p>
                    <h4 className="mt-1 text-xl font-bold text-slate-900">Resumen de tareas</h4>
                    <p className="mt-1 text-sm text-slate-500">Distribución entre tareas pendientes y completadas en la agenda actual.</p>
                  </div>
                </div>
                <div className="mt-4 h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={taskStatusData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={4}>
                        {taskStatusData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </SectionShell>
          ) : null}

          {activeStatsPanel === 'activity' ? (
            <SectionShell
              eyebrow="Actividad"
              title="Ritmo semanal del control"
              description="Acá ves en qué días se concentró el trabajo y dónde estuvo el mayor movimiento."
            >
              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md" onClick={() => onStatClick('Actividad de la Semana', weeklyActivityData)}>
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-600">Ritmo de operación</p>
                    <h4 className="mt-1 text-xl font-bold text-slate-900">Controles completados por día</h4>
                    <p className="mt-1 text-sm text-slate-500">Permite ver cómo se distribuye la ejecución semanal y dónde se concentró el mayor ritmo.</p>
                  </div>
                </div>
                <div className="mt-4 h-[340px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyActivityData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" stroke="#64748b" />
                      <YAxis allowDecimals={false} stroke="#64748b" />
                      <Tooltip cursor={{ fill: 'rgba(14,165,233,0.08)' }} />
                      <Bar dataKey="controles" fill="url(#weeklyGradient)" radius={[8, 8, 0, 0]} />
                      <defs>
                        <linearGradient id="weeklyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0f172a" stopOpacity={0.95} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.3} />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </SectionShell>
          ) : null}

          {activeStatsPanel === 'differences' ? (
            <SectionShell
              eyebrow="Diferencias"
              title="Productos con más desvíos"
              description="Una lectura rápida para ver dónde conviene revisar proceso, conteo o consistencia del sistema."
              rightAction={(
                <button onClick={() => setShowAllDifferencesModal(true)} className="inline-flex items-center gap-2 self-start rounded-full border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 transition hover:bg-violet-100">
                  <Icon path="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" className="h-5 w-5" />
                  Ver todas las diferencias
                </button>
              )}
            >
              <div className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topDifferencesData} layout="vertical" margin={{ top: 4, right: 16, left: 32, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" />
                    <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12, fill: '#475569' }} />
                    <Tooltip cursor={{ fill: 'rgba(244,63,94,0.08)' }} />
                    <Bar dataKey="diferencia" fill="#f43f5e" radius={[0, 8, 8, 0]} onClick={(data) => onBarClick(data.name)}>
                      {topDifferencesData.map((entry, index) => (
                        <Cell key={`diff-${entry.name}-${index}`} className="cursor-pointer" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionShell>
          ) : null}

          {activeStatsPanel === 'commercial' ? (
            <SectionShell
              eyebrow="Lectura comercial"
              title="Qué se vende mejor y dónde conviene mirar"
              description="Este bloque resume ventas, ganancia y proveedores del período para ayudar a decidir qué producto empujar, cuál revisar y dónde estás comprando más o más caro."
            >
              <div className="grid gap-4 xl:grid-cols-2">
                <RankingList title="Más vendidos" subtitle="Productos con más unidades entregadas en el período." rows={stockBalance?.topSellingProducts || []} accent="text-cyan-700" valueFormatter={(row) => formatCompactUnits(row.units)} secondaryFormatter={(row) => `${formatMoney(row.sales)} vendidos · ${formatMoney(row.margin)} de ganancia`} />
                <RankingList title="Menor salida" subtitle="Productos que tuvieron entregas, pero con menos movimiento." rows={stockBalance?.lowSellingProducts || []} accent="text-slate-700" valueFormatter={(row) => formatCompactUnits(row.units)} secondaryFormatter={(row) => `${formatMoney(row.sales)} vendidos · ${row.deliveries} entregas`} />
                <RankingList title="Mayor ganancia" subtitle="Productos que más margen dejaron en ventas completadas." rows={stockBalance?.topMarginProducts || []} accent="text-emerald-700" valueFormatter={(row) => formatMoney(row.margin)} secondaryFormatter={(row) => `${formatCompactUnits(row.units)} vendidas · ${formatMoney(row.sales)} facturados`} />
                <RankingList title="Ganancia más baja" subtitle="Productos con margen más corto o más apretado." rows={stockBalance?.lowMarginProducts || []} accent="text-amber-700" valueFormatter={(row) => formatMoney(row.margin)} secondaryFormatter={(row) => `${formatCompactUnits(row.units)} vendidas · ${formatMoney(row.sales)} facturados`} />
              </div>
              <div className="mt-4 grid gap-4 xl:grid-cols-3">
                <RankingList title="Dónde más comprás" subtitle="Proveedores con más gasto acumulado en compras recibidas." rows={stockBalance?.topSuppliers || []} accent="text-violet-700" valueFormatter={(row) => formatMoney(row.spend)} secondaryFormatter={(row) => `${formatCompactUnits(row.units)} ingresadas · ${row.operations} compras`} />
                <RankingList title="Mejor precio" subtitle="Proveedores con menor costo promedio por unidad en el período." rows={stockBalance?.bestPriceSuppliers || []} accent="text-emerald-700" valueFormatter={(row) => formatMoney(row.avgUnitCost)} secondaryFormatter={(row) => `${formatMoney(row.spend)} comprados · ${formatCompactUnits(row.units)}`} />
                <RankingList title="Precio más alto" subtitle="Proveedores con costo promedio más alto en el período." rows={stockBalance?.worstPriceSuppliers || []} accent="text-rose-700" valueFormatter={(row) => formatMoney(row.avgUnitCost)} secondaryFormatter={(row) => `${formatMoney(row.spend)} comprados · ${formatCompactUnits(row.units)}`} />
              </div>
            </SectionShell>
          ) : null}
        </>
      ) : (
        <>
          <SectionShell
            eyebrow="Balance"
            title="Tablero comercial del stock"
            description="Elegí una tarjeta y abajo ves solo ese bloque: resultado, stock guardado, productos/proveedores o alertas."
            rightAction={(
              <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Período realizado</span>
                <span className="mt-1 block font-semibold text-slate-800">{stockBalance?.periodLabel || 'Últimos 30 días'}</span>
              </div>
            )}
          >
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {balanceNavigatorCards.map((card) => (
                <ExecutiveCard
                  key={card.key}
                  title={card.title}
                  value={card.value}
                  helper={card.helper}
                  tone={card.tone}
                  impact={card.impact}
                  isActive={activeBalancePanel === card.key}
                  onClick={() => setActiveBalancePanel(card.key)}
                />
              ))}
            </div>
          </SectionShell>

          {activeBalancePanel === 'commercial' ? (
            <SectionShell
              eyebrow="Resultado comercial"
              title="Compras, ventas y ganancia del período"
              description="Una lectura rápida para entender cuánto salió en mercadería, cuánto ya se vendió y qué ganancia dejó ese movimiento."
            >
              <div className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#111827_52%,#1e293b_100%)] p-5 text-white shadow-[0_22px_55px_rgba(15,23,42,0.18)]">
                <div className="grid gap-4 xl:grid-cols-3">
                  {commercialPulseCards.map((card) => (
                    <div key={card.label} className={`rounded-[22px] border px-4 py-4 shadow-sm ${card.tone}`}>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-80">{card.label}</p>
                      <p className="mt-2 text-3xl font-bold leading-none">{card.value}</p>
                      <p className="mt-2 text-xs opacity-80">{card.helper}</p>
                      <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-white/50">
                        <div className={`h-full rounded-full bg-gradient-to-r ${card.fill}`} style={{ width: card.width }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {stockPeriodCards.map((card) => (
                  <div key={card.label} className={`rounded-[22px] border px-4 py-4 shadow-sm ${card.tone}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{card.label}</p>
                    <p className="mt-2 text-2xl font-bold leading-none">{card.value}</p>
                    <p className="mt-2 text-xs opacity-80">{card.helper}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <StockImpactAccordion
                  groups={stockBalanceTrace.commercialGroups}
                  tone="emerald"
                  emptyTitle="Todavía no hay impactos comerciales"
                  emptyDescription="Cuando entren compras o ventas en el período, acá vas a poder seguirlas día por día."
                />
              </div>
            </SectionShell>
          ) : null}

          {activeBalancePanel === 'stock' ? (
            <SectionShell
              eyebrow="Stock guardado"
              title="Costo, venta estimada y ganancia posible"
              description="Acá ves cuánto te costó el stock que hoy tenés guardado y cuánto podría rendir si lo vendieras al precio cargado."
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {stockBalanceCards.map((card) => (
                  <div key={card.label} className={`rounded-[22px] border px-4 py-4 shadow-sm ${card.tone}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{card.label}</p>
                    <p className="mt-2 text-2xl font-bold leading-none">{card.value}</p>
                    <p className="mt-2 text-xs opacity-80">{card.helper}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <StockImpactAccordion
                  groups={stockBalanceTrace.stockGroups}
                  tone="cyan"
                  formatAmount={(value) => `${Number(value || 0)} u.`}
                  emptyTitle="Todavía no hay movimientos que afecten el stock guardado"
                  emptyDescription="Cuando ingresen o salgan productos del depósito, acá vas a ver cómo suben o bajan las unidades guardadas."
                />
              </div>
            </SectionShell>
          ) : null}

          {activeBalancePanel === 'products' ? (
            <SectionShell
              eyebrow="Productos y proveedores"
              title="Qué líneas venden mejor y dónde conviene comprar"
              description="Te muestra rápido qué productos empujan la caja y qué proveedores tienen más peso o mejor costo."
            >
              <div className="grid gap-4 xl:grid-cols-2">
                <RankingList title="Productos que más venden" subtitle="Para identificar rápido qué empuja la caja." rows={stockBalance?.topSellingProducts || []} accent="text-cyan-700" valueFormatter={(row) => formatMoney(row.sales)} secondaryFormatter={(row) => `${formatCompactUnits(row.units)} entregadas · ${formatMoney(row.margin)} de ganancia`} />
                <RankingList title="Productos que más ganan" subtitle="Para ver qué líneas aportan mejor resultado." rows={stockBalance?.topMarginProducts || []} accent="text-emerald-700" valueFormatter={(row) => formatMoney(row.margin)} secondaryFormatter={(row) => `${formatCompactUnits(row.units)} vendidas · ${formatMoney(row.sales)} vendidos`} />
                <RankingList title="Dónde más comprás" subtitle="Proveedores con más gasto acumulado en compras recibidas." rows={stockBalance?.topSuppliers || []} accent="text-violet-700" valueFormatter={(row) => formatMoney(row.spend)} secondaryFormatter={(row) => `${formatCompactUnits(row.units)} ingresadas · ${row.operations} compras`} />
                <RankingList title="Mejor precio" subtitle="Proveedores con menor costo promedio por unidad." rows={stockBalance?.bestPriceSuppliers || []} accent="text-emerald-700" valueFormatter={(row) => formatMoney(row.avgUnitCost)} secondaryFormatter={(row) => `${formatMoney(row.spend)} comprados · ${formatCompactUnits(row.units)}`} />
              </div>
              <div className="mt-4">
                <StockImpactAccordion
                  groups={stockBalanceTrace.productGroups}
                  tone="violet"
                  formatAmount={(value) => `${Number(value || 0)} u.`}
                  emptyTitle="Todavía no hay movimientos por producto"
                  emptyDescription="Cuando entren o salgan productos en el período, acá vas a poder seguir la trazabilidad por día."
                />
              </div>
            </SectionShell>
          ) : null}

          {activeBalancePanel === 'alerts' ? (
            <SectionShell
              eyebrow="Alertas"
              title="Pendientes y consistencia del inventario"
              description="Este bloque marca lo que conviene revisar antes de confiar ciegamente en el balance: ingresos, salidas, estados especiales y datos faltantes."
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {stockAlertCards.map((card) => (
                  <div key={card.label} className={`rounded-[22px] border px-4 py-4 shadow-sm ${card.tone}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{card.label}</p>
                    <p className="mt-2 text-2xl font-bold leading-none">{card.value}</p>
                    <p className="mt-2 text-xs opacity-80">{card.helper}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {stockBalanceTrace.alertsRows.length > 0 ? (
                  stockBalanceTrace.alertsRows.map((alert) => (
                    <div key={alert.id} className="rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-4 shadow-sm">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-700">Alerta activa</p>
                      <p className="mt-2 text-lg font-bold text-rose-950">{alert.label}</p>
                      <p className="mt-1 text-sm text-rose-800">{alert.detail}</p>
                    </div>
                  ))
                ) : (
                  <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-5 py-6 text-center md:col-span-2">
                    <p className="text-base font-bold text-slate-900">No hay alertas activas</p>
                    <p className="mt-2 text-sm text-slate-500">Cuando aparezcan pendientes o datos inconsistentes, este bloque te los va a dejar visibles al instante.</p>
                  </div>
                )}
              </div>
            </SectionShell>
          ) : null}
        </>
      )}

      <AllDifferencesModal
        show={showAllDifferencesModal}
        onClose={() => setShowAllDifferencesModal(false)}
        allDifferencesData={allDifferencesData}
        onProductClick={onBarClick}
      />
    </div>
  );
};

export default StatisticsView;
